import CompanyLogo from "@components/company_logo.tsx";
import { Tag } from "@sprout/tag";
import { SecondaryButton } from "@sprout/buttons";
import Divider from "@sprout/divider.tsx";
import useTranslation from "@/hooks/use_translation";

const WORK_TYPE_I18N_KEY: Record<string, string> = {
  remote: "workTypeOptions.remote",
  hybrid: "workTypeOptions.hybrid",
  in_person: "workTypeOptions.inPerson",
};

export type JobRecommendation = {
  id: number;
  title: string;
  companyName: string;
  logoUrl: string;
  publicUrl: string;
  viewJobPath: string;
  urlToken: string;
  locations: string[];
  workType: string | null;
  payRanges: string | null;
  viewed: boolean;
  description?: string;
};

type Props = {
  job: JobRecommendation;
  onViewJob: () => void;
  isLoading?: boolean;
};

const JobRecommendationCard = ({ job, onViewJob, isLoading = false }: Props) => {
  const { t } = useTranslation("jobSearch");

  const workTypeLabel = job.workType ? t(WORK_TYPE_I18N_KEY[job.workType]) : null;
  const visibleLocation = job.locations[0] ?? null;
  const extraLocations = job.locations.length - 1;

  return (
    <div className="bg-white border border-[var(--object-strokes-dividers,#ccd4d1)] rounded-lg p-6 flex flex-col gap-4 w-full">
      <div className="flex gap-3 items-center">
        <CompanyLogo companyName={job.companyName} logoUrl={job.logoUrl} size="small" />
        <div className="flex flex-col min-w-0">
          <p className="font-medium text-base leading-6 text-[var(--text-primary,#15372c)] truncate">
            {job.title}
          </p>
          <p className="text-sm leading-5 text-[var(--text-primary,#15372c)] truncate">
            {job.companyName}
          </p>
        </div>
      </div>

      {job.description && (
        <p className="text-sm leading-5 text-[var(--text-primary,#15372c)] line-clamp-3">
          {job.description}
        </p>
      )}

      <div className="flex flex-row gap-2 items-center flex-wrap">
        {workTypeLabel && (
          <Tag label={workTypeLabel} truncate={false} className="whitespace-nowrap" />
        )}
        {visibleLocation && (
          <Tag label={visibleLocation} truncate={false} className="whitespace-nowrap" />
        )}
        {extraLocations > 0 && (
          <span className="text-xs text-[var(--text-primary,#15372c)] whitespace-nowrap">
            {t("moreLocations", { count: extraLocations })}
          </span>
        )}
        {job.payRanges && (
          <Tag
            label={job.payRanges}
            truncate={false}
            color="var(--green-50)"
            className="whitespace-nowrap"
          />
        )}
      </div>

      <Divider />

      <div className="flex justify-center">
        <SecondaryButton
          size="large"
          shape="rounded"
          onClick={onViewJob}
          disabled={isLoading}
          loading={isLoading}
        >
          {t("viewJob")}
        </SecondaryButton>
      </div>
    </div>
  );
};

export default JobRecommendationCard;
